import { useState } from "react";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SceneBackdrop from "@/components/SceneBackdrop";

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_OPTIONS = [
  "TheraTec Aura Chamber",
  "TheraTec Space Capsule",
  "TheraTec Atlas Chamber",
  "TheraO3 Ozone Sauna",
  "THERATEC Vibration Recovery Plate",
  "Full clinic build-out",
  "Not sure yet",
];

export default function FinancingSection() {
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    product_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/financing/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSuccess(true);
      toast.success("Financing inquiry received — our team will be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="financing"
      data-testid="financing-section"
      className="relative overflow-hidden bg-[#F5F3EE] text-[#1C1C1C] py-24 lg:py-32"
    >
      <SceneBackdrop variant="sand" label="REPLENISH" art="wave" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Left copy */}
        <div>
          <div className="overline text-[#C9A961]">Flexible Capital</div>
          <h2 className="font-serif-editorial text-4xl lg:text-5xl mt-4 leading-tight">
            Need financing?<br />
            <em className="italic tt-shimmer-gold">Let's structure it.</em>
          </h2>
          <p className="mt-8 text-base text-[#5C5C5C] leading-relaxed max-w-md">
            Premium equipment, premium terms. Tell us what you're building and we'll match you with a qualified lender specialised in med-spa capital — 24-month, 36-month and 60-month plans available.
          </p>
          <ul className="mt-10 space-y-4 text-sm">
            {[
              "Approvals in 24–48 hours",
              "Zero down and deferred-start options",
              "Section 179 tax-deductible on qualifying equipment",
              "Dedicated concierge from application through install",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-[#1C1C1C]/85">
                <CheckCircle2 size={16} strokeWidth={1.5} className="text-[#C9A961] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* Approve partner block */}
          <div
            data-testid="financing-partner-approve"
            className="mt-10 pt-8 border-t border-[#D8D3C7] flex items-center gap-5"
          >
            <div className="bg-white rounded-md px-4 py-3 flex items-center justify-center shrink-0 border border-[#E5E2DA]">
              <img
                src="https://customer-assets.emergentagent.com/job_luxe-med-spa/artifacts/nxfb31cm_IMG_1381.jpeg"
                alt="Financing powered by Approve"
                className="h-11 w-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="text-sm text-[#5C5C5C] leading-relaxed">
              <div className="overline text-[#C9A961] mb-1">Financing Partner</div>
              We partner with <span className="text-[#1C1C1C] font-medium">Approve</span> for easy, fast financing options tailored to premium med-spa equipment.
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="bg-white border border-[#D8D3C7] p-8 lg:p-10 shadow-sm">
          {success ? (
            <div
              data-testid="financing-success"
              className="flex flex-col items-center text-center py-16"
              style={{ animation: "tt-fade-up 500ms ease both" }}
            >
              <div className="h-14 w-14 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
                <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#C9A961]" />
              </div>
              <h3 className="mt-6 font-serif-editorial text-2xl">Application received</h3>
              <p className="mt-3 text-sm text-[#5C5C5C] max-w-sm">
                A financing concierge will reach out within 24 hours at the details you provided. Check your inbox for a confirmation email.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setForm({ full_name: "", business_name: "", email: "", phone: "", product_interest: "", message: "" });
                }}
                data-testid="financing-reset"
                className="mt-8 overline text-[#C9A961] hover:text-[#1C1C1C] transition-colors"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="financing-form" className="space-y-5">
              <div className="overline text-[#5C5C5C]">Financing Inquiry</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" name="full_name" value={form.full_name} onChange={handleChange} required data-testid="financing-full-name" />
                <Field label="Business / med spa" name="business_name" value={form.business_name} onChange={handleChange} required data-testid="financing-business" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="financing-email" />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required data-testid="financing-phone" />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.14em] uppercase text-[#5C5C5C] mb-2">Product of interest</label>
                <select
                  name="product_interest"
                  value={form.product_interest}
                  onChange={handleChange}
                  data-testid="financing-product"
                  className="w-full bg-white border border-[#D8D3C7] px-3 py-3 text-sm text-[#1C1C1C] focus:outline-none focus:border-[#C9A961] transition-colors"
                >
                  <option value="">Select equipment…</option>
                  {PRODUCT_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.14em] uppercase text-[#5C5C5C] mb-2">Message (optional)</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Timeline, budget, or anything else we should know…"
                  data-testid="financing-message"
                  className="w-full bg-white border border-[#D8D3C7] px-3 py-3 text-sm text-[#1C1C1C] placeholder:text-[#A8A096] focus:outline-none focus:border-[#C9A961] transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="financing-submit"
                className="w-full mt-2 bg-[#1C1C1C] text-[#FAF9F6] py-4 flex items-center justify-center gap-3 font-medium tracking-wide hover:bg-[#C9A961] hover:text-[#1C1C1C] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit inquiry
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[#A8A096] pt-2">
                By submitting, you consent to be contacted by TheraTec about financing options. We do not run a hard credit pull.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.14em] uppercase text-[#5C5C5C] mb-2">
        {label}
      </label>
      <input
        {...props}
        className="w-full bg-white border border-[#D8D3C7] px-3 py-3 text-sm text-[#1C1C1C] placeholder:text-[#A8A096] focus:outline-none focus:border-[#C9A961] transition-colors"
      />
    </div>
  );
}
